import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Sparkles, ArrowRight, Flame } from "lucide-react";
import { useLang } from "@/context/LangContext";

const COLLECTIONS = [
  {
    key: "digital-subscription",
    title: "Digital Subscription",
    subtitle: "Indie Indian OTT, magazines, apps and creator platforms",
    img: "https://images.unsplash.com/photo-1607252650355-f7fd0460ccdb?w=800",
    tag: "digital",
    accent: "#E25A24",
  },
  {
    key: "food-dining",
    title: "Food & Dining",
    subtitle: "Homegrown restaurants, meal kits, artisanal snacks & sauces",
    img: "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=800",
    tag: "food",
    accent: "#275F36",
  },
  {
    key: "handlooms-fashions",
    title: "Handlooms & Fashions",
    subtitle: "Handwoven fits, block-print streetwear, Kolhapuri drops",
    img: "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=800",
    tag: "fashion",
    accent: "#B44618",
  },
  {
    key: "handicrafts",
    title: "Handicrafts",
    subtitle: "Terracotta, blue pottery, oxidised silver, statement pieces",
    img: "https://images.unsplash.com/photo-1599643477877-530eb83abc8e?w=800",
    tag: "handicrafts",
    accent: "#13294B",
  },
  {
    key: "yatras",
    title: "Yatras",
    subtitle: "Curated pilgrimages, temple trails, artisan-village trips",
    img: "https://images.unsplash.com/photo-1548013146-72479768bada?w=800",
    tag: "yatra",
    accent: "#7C3F58",
  },
];

export default function GenZ() {
  const { t } = useLang();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { q: "genz", limit: 12 } })
      .then((r) => setProducts(r.data))
      .catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0D141C] text-white">
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #E25A24 0, transparent 40%), radial-gradient(circle at 80% 70%, #B44618 0, transparent 45%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-xs font-bold uppercase tracking-widest">
              <Flame className="w-3.5 h-3.5 text-[#FFB07C]" /> Gen Z drop
            </div>
            <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-white">
              Swadeshi, <br/>
              <span className="text-[#FFB07C]">but make it</span> Gen&nbsp;Z.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
              Handmade in India by artisans, curated for the generation that
              cares where things come from. Streetwear, sneakers, skincare,
              statement jewellery — all rooted in India, none of it mid.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/products"
                data-testid="genz-shop-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FFB07C] text-[#0D141C] font-bold hover:bg-white transition-colors"
              >
                Shop the drop <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#collections"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors"
              >
                See collections
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              <div>
                <div className="font-serif text-3xl text-[#FFB07C]">₹499+</div>
                <div className="text-xs uppercase tracking-widest text-white/60 mt-1">Starter picks</div>
              </div>
              <div>
                <div className="font-serif text-3xl text-[#FFB07C]">100%</div>
                <div className="text-xs uppercase tracking-widest text-white/60 mt-1">Made in India</div>
              </div>
              <div>
                <div className="font-serif text-3xl text-[#FFB07C]">0</div>
                <div className="text-xs uppercase tracking-widest text-white/60 mt-1">Fast fashion</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COLLECTIONS BENTO */}
      <section id="collections" className="max-w-7xl mx-auto px-6 py-20">
        <div className="mb-10">
          <div className="label-caps mb-2">The vibe</div>
          <h2 className="text-3xl md:text-4xl">Collections built for the algorithm-aware</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="genz-collections">
          {COLLECTIONS.map((c) => (
            <Link
              key={c.key}
              to={`/products?q=${encodeURIComponent(c.tag)}`}
              data-testid={`genz-collection-${c.key}`}
              className="group relative aspect-[4/5] rounded-2xl overflow-hidden card-flat"
            >
              <img
                src={c.img}
                alt={c.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(180deg, transparent 0%, rgba(13,20,28,0.15) 55%, ${c.accent}e6 100%)`,
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="text-xs uppercase tracking-widest opacity-80">Collection</div>
                <div className="font-serif text-2xl md:text-3xl leading-tight mt-1">{c.title}</div>
                <div className="text-sm opacity-90 mt-2 leading-snug">{c.subtitle}</div>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                  Explore <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TRENDING PRODUCTS */}
      {products.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-20">
          <div className="mb-10 flex items-end justify-between">
            <div>
              <div className="label-caps mb-2 inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3 text-[#E25A24]" /> Trending on the feed</div>
              <h2 className="text-3xl md:text-4xl">Actually good picks</h2>
            </div>
            <Link to="/products" className="text-sm text-[#E25A24] font-semibold hover:underline">Shop all →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="genz-products">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* MANIFESTO */}
      <section className="bg-[#F2EFEB] py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="label-caps mb-4">Why bother</div>
          <h2 className="text-4xl md:text-5xl leading-tight">
            Because the label matters more than the logo.
          </h2>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
            Every piece here is made by hand, in India, by someone whose name we know.
            You can see their workshop, hear their story, and pay them directly — no
            middlemen, no fast-fashion cycles, no vague "sustainable" claims. Just real
            things, made really well, by real people.
          </p>
          <Link
            to="/products"
            className="mt-10 btn-primary inline-flex items-center gap-2"
            data-testid="genz-cta-bottom"
          >
            Enter the bazaar <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
